import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Loader2, CheckCircle, ShoppingBag, MapPin, Truck, Package, UserPlus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { StateCitySelect } from '@/components/shared/StateCitySelect';
import { useVatRate } from '@/hooks/useVatRate';
import { DeliveryOptions } from '@/components/checkout/DeliveryOptions';


const nigerianStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
  'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const deliverySchema = z.object({
  fullName: z.string().min(2, 'Full name is required'),
  phone: z.string().regex(/^(\+234|0)[789]\d{9}$/, 'Enter a valid Nigerian phone number'),
  addressLine1: z.string().min(5, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  postalCode: z.string().optional(),
});

type DeliveryFormData = z.infer<typeof deliverySchema>;

export default function CheckoutFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState<string | null>(null);
  const [isInitializingPayment, setIsInitializingPayment] = useState(false);
  const [productData, setProductData] = useState<any>(null);
  const [sellerData, setSellerData] = useState<any>(null);
  
  // Delivery state for buyNow flow
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [sellerPickupState, setSellerPickupState] = useState('Lagos');
  
  // Account creation fields for new buyers
  const [buyerAccountEmail, setBuyerAccountEmail] = useState('');
  const [buyerAccountPassword, setBuyerAccountPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
      if (user?.email) setBuyerAccountEmail(user.email);
    };
    checkAuth();
  }, []);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<{
    id: string; name: string; price: number; estimatedDays: string; provider?: 'fez' | 'self';
  } | null>(null);
  
  const { vatRate, vatEnabled, calculateVat } = useVatRate();

  // Prefill recipient details from logged-in user's profile
  useEffect(() => {
    const prefillFromProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', user.id)
        .maybeSingle();
      if (profile) {
        if (profile.full_name && !recipientName) setRecipientName(profile.full_name);
        if (profile.phone && !recipientPhone) setRecipientPhone(profile.phone);
        // Also prefill the post-payment delivery form
        if (profile.full_name) setValue('fullName', profile.full_name);
        if (profile.phone) setValue('phone', profile.phone);
      }
    };
    prefillFromProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset delivery option when location changes
  useEffect(() => {
    setSelectedDeliveryOption(null);
  }, [deliveryState, deliveryCity]);
  
  const reference = searchParams.get('reference');
  const status = searchParams.get('status');
  const productId = searchParams.get('productId');
  const buyNow = searchParams.get('buyNow') === 'true';
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<DeliveryFormData>({
    resolver: zodResolver(deliverySchema)
  });

  // No need for reset effect - useFezDeliveryRate handles location changes automatically

  // Initialize payment session for buyNow flow (WhatsApp shares)
  const initializePaymentForBuyNow = useCallback(async () => {
    if (!productId) return;
    
    setIsInitializingPayment(true);
    try {
      // Fetch product details using secure RPC (works for anon users too)
      const { data: rpcProducts, error: productError } = await supabase.rpc('get_public_product_by_id', {
        p_product_id: productId
      });
      const product = rpcProducts?.[0] || null;
      
      if (productError || !product) {
        console.error('Product not found:', productError);
        toast.error('Product not found or no longer available');
        navigate('/');
        return;
      }
      
      setProductData(product);
      
      // Seller info is already included in the RPC result
      if (product.business_name) {
        setSellerData({
          business_name: product.business_name,
          business_description: product.business_description || '',
          business_category: '',
          verified: product.seller_verified || false,
          rating: product.seller_rating || 0,
        });
      }
      
      // Seller pickup state is included in the RPC result
      if (product.seller_pickup_state) {
        setSellerPickupState(product.seller_pickup_state);
      }
      
    } catch (error) {
      console.error('Error initializing buy now:', error);
      toast.error('Failed to load product details');
      navigate('/');
    } finally {
      setIsInitializingPayment(false);
    }
  }, [productId, navigate]);

  // Calculate totals for buyNow flow
  const productPrice = productData?.price || 0;
  const deliveryFee = selectedDeliveryOption?.price || 0;
  const hasDeliveryRate = !!selectedDeliveryOption && deliveryFee > 0;
  const vat = vatEnabled ? calculateVat(productPrice) : 0;
  const grandTotal = productPrice + deliveryFee + vat;

  // Proceed to Paystack payment
  const handleProceedToPayment = async () => {
    if (!productData) return;
    
    // Validate all 5 required delivery fields
    const missingFields: string[] = [];
    if (!recipientName.trim()) missingFields.push('full name');
    if (!recipientPhone.trim()) missingFields.push('phone number');
    if (!deliveryAddress.trim()) missingFields.push('street address');
    if (!deliveryState) missingFields.push('state');
    if (!deliveryCity) missingFields.push('city');

    if (missingFields.length > 0) {
      toast.error(`Please complete your delivery information to continue. Missing: ${missingFields.join(', ')}`);
      return;
    }

    const phoneRegex = /^(\+234|0)[789]\d{9}$/;
    if (!phoneRegex.test(recipientPhone.trim())) {
      toast.error('Please enter a valid Nigerian phone number');
      return;
    }
    
    if (!selectedDeliveryOption) {
      toast.error('Please select a delivery option');
      return;
    }
    
    // Validate account fields for non-logged-in users
    if (!isLoggedIn) {
      if (!buyerAccountEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerAccountEmail.trim())) {
        toast.error('Please enter a valid email address to create your account');
        return;
      }
      if (!buyerAccountPassword || buyerAccountPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    
    setIsInitializingPayment(true);
    try {
      // Use real email for payment
      const paymentEmail = buyerAccountEmail.trim() || `guest_${Date.now()}@sabitradeng.com`;
      
      // Store credentials in sessionStorage for account creation after payment
      if (!isLoggedIn && buyerAccountEmail.trim() && buyerAccountPassword) {
        sessionStorage.setItem('pending_buyer_email', buyerAccountEmail.trim());
        sessionStorage.setItem('pending_buyer_password', buyerAccountPassword);
        sessionStorage.setItem('pending_buyer_name', recipientName.trim());
        sessionStorage.setItem('pending_buyer_phone', recipientPhone.trim());
      }
      
      const { data, error } = await supabase.functions.invoke('generate-payment-link', {
        body: {
          productId: productData.id,
          quantity: 1,
          email: paymentEmail,
          deliveryFee: deliveryFee,
          deliveryType: selectedDeliveryOption?.provider === 'fez' ? 'fez' : 'self',
          deliveryAddress: {
            address: deliveryAddress,
            addressLine1: deliveryAddress,
            state: deliveryState,
            city: deliveryCity,
            country: 'Nigeria'
          },
          recipientName: recipientName.trim(),
          recipientPhone: recipientPhone.trim()
        }
      });
      
      console.log('Payment init response:', data);
      
      if (error) throw error;
      
      // The edge function returns paymentLink, not authorization_url
      const paymentUrl = data?.paymentLink || data?.payment_url || data?.authorization_url;
      
      if (paymentUrl) {
        // Redirect to Paystack
        window.location.href = paymentUrl;
      } else {
        console.error('Full response without payment URL:', data);
        throw new Error('No payment URL received from payment service');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error('Failed to initialize payment. Please try again.');
      setIsInitializingPayment(false);
    }
  };

  useEffect(() => {
    if (reference) {
      // Always verify payment when we have a reference
      verifyPayment();
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
      navigate('/');
    } else if (buyNow && productId && !reference) {
      // WhatsApp share flow: no payment reference yet, initialize the checkout
      initializePaymentForBuyNow();
    }
  }, [reference, buyNow, productId, initializePaymentForBuyNow]);

  const verifyPayment = async () => {
    if (!reference) return;
    
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { reference }
      });

      console.log('Payment verification response:', data);
      
      if (error) {
        console.error('Payment verification error:', error);
        throw error;
      }
      
      if (data?.status === 'success' && data.order) {
        setOrderDetails(data.order);
        setBuyerEmail(data?.payment?.customerEmail || null);
        setPaymentVerified(true);
        toast.success('Payment verified successfully! Please provide delivery details.');
      } else {
        console.error('Invalid payment verification response:', data);
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment. Please contact support.');
      navigate('/');
    } finally {
      setIsVerifying(false);
    }
  };

  const onSubmit = async (data: DeliveryFormData) => {
    if (!orderDetails || !paymentVerified) {
      toast.error('Please complete payment verification first');
      return;
    }
    
    setIsProcessing(true);
    let sellerId: string | null = null;
    let newOrder: any = null;
    
    try {
      // Check if this is a temporary order (guest checkout)
      const isTemporaryOrder = orderDetails.id?.startsWith('temp-');
      
      if (isTemporaryOrder) {
        // Extract product information from custom_fields
        let productName = 'Product';
        let quantity = 1;
        let sellerName = 'Seller';
        sellerId = orderDetails.seller_id;
        
        console.log('Processing temporary order:', orderDetails);
        
        if (orderDetails.items?.custom_fields) {
          const fields = orderDetails.items.custom_fields;
          productName = fields.find((f: any) => f.variable_name === 'product_name')?.value || 'Product';
          quantity = parseInt(fields.find((f: any) => f.variable_name === 'quantity')?.value || '1');
          sellerName = fields.find((f: any) => f.variable_name === 'seller_name')?.value || 'Seller';
          
          console.log('Extracted fields:', { productName, quantity, sellerName });
        }
        
        // If no seller_id, try to find seller by business name
        if ((!sellerId || sellerId === 'null') && sellerName && sellerName !== 'Seller') {
          console.log('Looking up seller by business name:', sellerName);
          const { data: sellers, error: sellerError } = await supabase
            .from('profiles')
            .select('id')
            .eq('business_name', sellerName)
            .not('business_name', 'is', null)
            .maybeSingle();
          
          if (!sellerError && sellers) {
            sellerId = sellers.id;
            console.log('Found seller ID:', sellerId);
          } else {
            console.log('Could not find seller:', sellerError || 'No matching seller');
            // Set to null if not found to avoid UUID errors
            sellerId = null;
          }
        } else if (sellerId === 'null' || sellerId === undefined) {
          sellerId = null;
        }
        
        // For guest checkout, create a new order with delivery information
        // Preserve delivery_type from the original order details (set during checkout)
        const resolvedDeliveryType = orderDetails.delivery_type || 'fez';
        console.log('Temp order delivery_type resolved to:', resolvedDeliveryType);
        const orderData: any = {
          buyer_id: null, // Guest checkout
          items: [{
            productId: null,
            product: {
              id: null,
              title: productName,
              price: orderDetails.total_amount / quantity,
              description: productName,
              images: [],
              category: 'General'
            },
            quantity: quantity,
            price: orderDetails.total_amount / quantity
          }],
          total_amount: orderDetails.total_amount,
          delivery_type: resolvedDeliveryType,
          status: 'confirmed',
          payment_status: 'completed',
          payment_reference: orderDetails.payment_reference,
          payment_method: 'paystack',
          shipping_address: {
            fullName: data.fullName,
            phone: data.phone,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            country: 'Nigeria'
          },
          // Structured delivery fields for Fez
          delivery_address_line: data.addressLine1,
          delivery_city: data.city,
          delivery_state: data.state,
          delivery_phone: data.phone,
          recipient_name: data.fullName,
          delivery_address: [data.addressLine1, data.city, data.state].filter(Boolean).join(', '),
          estimated_delivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        };
        
        // Only include seller_id if it's a valid UUID (not null or 'null' string)
        if (sellerId && sellerId !== 'null' && sellerId !== null && sellerId !== undefined) {
          orderData.seller_id = sellerId;
        } else {
          // Don't include seller_id at all if invalid to avoid UUID errors
          console.log('No valid seller_id, creating order without seller reference');
        }
        
        console.log('Creating new order with data:', orderData);
        
        const { data: orderCreated, error: createError } = await supabase
          .from('orders')
          .insert(orderData)
          .select()
          .single();

        if (createError) {
          console.error('Error creating order:', createError);
          throw createError;
        }
        newOrder = orderCreated;
        setOrderDetails(newOrder);
        
        // Create tracking entry for the new order
        const trackingNumber = `TRK${newOrder.id.slice(0, 8).toUpperCase()}`;
        await supabase
          .from('tracking')
          .insert({
            order_id: newOrder.id,
            tracking_number: trackingNumber,
            status: 'order_placed',
            location: data.city + ', ' + data.state,
            description: 'Order has been placed and is being processed',
            estimated_delivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          });
      } else {
        // Update existing order with delivery information
        const { error: updateError } = await supabase
          .from('orders')
          .update({
            shipping_address: {
              fullName: data.fullName,
              phone: data.phone,
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              city: data.city,
              state: data.state,
              postalCode: data.postalCode,
              country: 'Nigeria'
            },
            // Structured delivery fields for Fez
            delivery_address_line: data.addressLine1,
            delivery_city: data.city,
            delivery_state: data.state,
            delivery_phone: data.phone,
            recipient_name: data.fullName,
            delivery_address: [data.addressLine1, data.city, data.state].filter(Boolean).join(', '),
            status: 'confirmed',
            estimated_delivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          })
          .eq('id', orderDetails.id);

        if (updateError) throw updateError;

        // Create tracking entry
        const trackingNumber = `TRK${orderDetails.id.slice(0, 8).toUpperCase()}`;
        const { error: trackingError } = await supabase
          .from('tracking')
          .insert({
            order_id: orderDetails.id,
            tracking_number: trackingNumber,
            status: 'order_placed',
            location: data.city + ', ' + data.state,
            description: 'Order has been placed and is being processed',
            estimated_delivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          });

        if (trackingError) {
          console.error('Error creating tracking:', trackingError);
        }
      }

      // Notify seller about the order with delivery details
      const finalOrderId = newOrder?.id || orderDetails.id;
      const finalSellerId = sellerId || orderDetails.seller_id;
      
      // Validate seller ID is a valid UUID before sending notification
      const isValidUUID = finalSellerId && 
                         finalSellerId !== 'null' && 
                         finalSellerId !== null && 
                         finalSellerId !== undefined &&
                         /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(finalSellerId);
      
      if (isValidUUID) {
        console.log('Notifying seller:', finalSellerId);
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert({
            user_id: finalSellerId,
            title: 'New Order Ready for Fulfillment',
            message: `Order #${finalOrderId.slice(0, 8)} has been confirmed with delivery to ${data.city}, ${data.state}. Please prepare for shipment.`,
            type: 'order',
            severity: 'success',
            metadata: {
              order_id: finalOrderId,
              amount: orderDetails.total_amount,
              delivery_address: {
                city: data.city,
                state: data.state,
                phone: data.phone,
                fullName: data.fullName
              }
            },
            link: '/seller/orders'
          });

        if (notificationError) {
          console.error('Error notifying seller:', notificationError);
          // Don't fail the order if notification fails
        }
      } else {
        console.log('No valid seller ID for notification');
      }

      // Send WhatsApp notification to buyer if phone is provided
      if (data.phone) {
        try {
          const { data: whatsappResult, error: whatsappError } = await supabase.functions.invoke('send-whatsapp-receipt', {
            body: {
              orderId: orderDetails.id,
              phoneNumber: data.phone,
              paymentReference: orderDetails.payment_reference
            }
          });
          
          if (whatsappError) {
            console.warn('WhatsApp receipt not sent (service may not be configured):', whatsappError);
          } else {
            console.log('WhatsApp receipt sent successfully');
          }
        } catch (error) {
          console.warn('WhatsApp service not available:', error);
          // Don't fail the order if WhatsApp fails
        }
      }

      // Send confirmation email to buyer
      if (buyerEmail) {
        try {
          await supabase.functions.invoke('send-transactional-email', {
            body: {
              to: buyerEmail,
              type: 'order-confirmation',
              data: {
                orderNumber: `ORD${(newOrder?.id || orderDetails.id).slice(0, 8).toUpperCase()}`,
                customerName: data.fullName,
                total: newOrder?.total_amount || orderDetails.total_amount,
                items: Array.isArray(orderDetails.items) ? orderDetails.items : [],
                trackingUrl: `https://sabitradeng.com/track-order/${newOrder?.id || orderDetails.id}`
              }
            }
          });
        } catch (e) {
          console.warn('Email confirmation not sent:', e);
        }
      }

      setOrderComplete(true);
      toast.success('Order confirmed successfully!');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to confirm order');
    } finally {
      setIsProcessing(false);
    }
  };

  if (orderComplete) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Breadcrumbs />
            <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
            <p className="text-muted-foreground mb-4">
              Your order has been successfully placed. You'll receive updates via SMS.
            </p>
            <p className="text-sm mb-6">
              Tracking Number: <span className="font-mono font-bold">TRK{orderDetails?.id.slice(0, 8).toUpperCase()}</span>
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/track-order')}
                className="w-full"
              >
                Track Your Order
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>
           </CardContent>
         </Card>
        </div>
      </div>
    );
  }

  // Show loading state while verifying payment
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Breadcrumbs />
          <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Verifying Payment...</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment
            </p>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Show loading state while initializing buy now flow
  if (isInitializingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Breadcrumbs />
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Preparing Checkout...</h2>
              <p className="text-muted-foreground">
                Please wait while we set up your order
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show product preview for buyNow flow before payment
  if (buyNow && productId && !reference && productData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 px-3 py-4 sm:p-4">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
          <Breadcrumbs />
          
          {/* Product Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                Quick Checkout
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Select your delivery location and proceed to payment
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Product Preview */}
              <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg">
                {productData.images?.[0] && (
                  <img 
                    src={productData.images[0]} 
                    alt={productData.title}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base line-clamp-2">{productData.title}</h3>
                  {sellerData?.business_name && (
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Sold by {sellerData.business_name}
                    </p>
                  )}
                  <p className="text-base sm:text-lg font-bold text-primary mt-1">
                    ₦{productData.price?.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recipient Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Recipient Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recipientName">Full Name *</Label>
                  <Input
                    id="recipientName"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Enter recipient name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipientPhone">Phone Number *</Label>
                  <Input
                    id="recipientPhone"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="e.g., 08012345678"
                  />
                </div>
              </div>
              
              {/* Account Creation Fields - Only for non-logged-in users */}
              {!isLoggedIn && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Create Your Account
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Sign up to track your order and manage future purchases
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="buyerEmail">Email Address *</Label>
                      <Input
                        id="buyerEmail"
                        type="email"
                        value={buyerAccountEmail}
                        onChange={(e) => setBuyerAccountEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="buyerPassword">Password *</Label>
                      <Input
                        id="buyerPassword"
                        type="password"
                        value={buyerAccountPassword}
                        onChange={(e) => setBuyerAccountPassword(e.target.value)}
                        placeholder="Min. 6 characters"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Delivery Location Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <StateCitySelect
                stateValue={deliveryState}
                cityValue={deliveryCity}
                onStateChange={setDeliveryState}
                onCityChange={setDeliveryCity}
                stateLabel="State"
                cityLabel="City"
              />
              
              {/* Street Address Input */}
              <div className="space-y-2">
                <Label htmlFor="deliveryAddress">Street Address *</Label>
                <Input
                  id="deliveryAddress"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="123 Main Street, Area/Landmark"
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Delivery Options (show when location selected) */}
          {deliveryState && deliveryCity && productData && (
            <DeliveryOptions
              sellerId={productData.seller_id}
              sellerState={sellerPickupState}
              destinationState={deliveryState}
              destinationCity={deliveryCity}
              destinationAddress={deliveryAddress}
              items={[{ quantity: 1, price: productData.price, title: productData.title }]}
              onSelectOption={(option) => setSelectedDeliveryOption(option)}
              selectedOptionId={selectedDeliveryOption?.id}
            />
          )}

          {/* Order Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Subtotal</span>
                  <span>₦{productPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    Delivery
                  </span>
                  {selectedDeliveryOption ? (
                    <span>₦{deliveryFee.toLocaleString()}</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">Select location</span>
                  )}
                </div>
                {selectedDeliveryOption && (
                  <div className="text-xs text-muted-foreground">
                    {selectedDeliveryOption.name} • {selectedDeliveryOption.estimatedDays}
                  </div>
                )}
                {vatEnabled && (
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>VAT ({vatRate}%)</span>
                    <span>₦{Math.round(vat).toLocaleString()}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-base sm:text-lg">
                  <span>Total</span>
                  <span>₦{Math.round(grandTotal).toLocaleString()}</span>
                </div>
              </div>

              {/* Fez validation warning */}
              {selectedDeliveryOption?.provider === 'fez' && (!recipientName.trim() || !recipientPhone.trim() || !deliveryAddress.trim() || !deliveryState || !deliveryCity) && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span>Please complete delivery details for Fez shipping</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button 
                  onClick={handleProceedToPayment}
                  className="w-full"
                  size="lg"
                  disabled={isInitializingPayment || !selectedDeliveryOption || !deliveryAddress.trim() || !recipientName.trim() || !recipientPhone.trim() || !deliveryState || !deliveryCity}
                >
                  {isInitializingPayment ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : !recipientName.trim() || !recipientPhone.trim() ? (
                    'Enter Recipient Details'
                  ) : !deliveryState || !deliveryCity ? (
                    'Enter Delivery Location'
                  ) : !deliveryAddress.trim() ? (
                    'Enter Street Address'
                  ) : !selectedDeliveryOption ? (
                    'Select Delivery Option'
                  ) : (
                    `Pay ₦${Math.round(grandTotal).toLocaleString()}`
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/products/${productId}`)}
                >
                  View Product Details
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show error if no payment reference and not in buyNow flow
  if (!reference && !buyNow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Breadcrumbs />
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <h2 className="text-xl font-bold mb-2">Invalid Payment Session</h2>
              <p className="text-muted-foreground mb-4">
                No payment reference found. Please restart the checkout process.
              </p>
              <Button onClick={() => navigate('/')}>Go to Homepage</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If buyNow but no product loaded yet, show loading
  if (buyNow && productId && !reference && !productData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Breadcrumbs />
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Loading Product...</h2>
              <p className="text-muted-foreground">
                Please wait
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 px-3 py-4 sm:p-4">
      <div className="max-w-2xl mx-auto">
        <Breadcrumbs />
        <Card>
          <CardHeader>
            <CardTitle>Delivery Information</CardTitle>
            <p className="text-sm text-muted-foreground">
              {paymentVerified 
                ? "Payment confirmed! Please provide your delivery details to complete your order"
                : "Processing payment verification..."}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    {...register('fullName')}
                    placeholder="John Doe"
                  />
                  {errors.fullName && (
                    <p className="text-sm text-destructive">{errors.fullName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    {...register('phone')}
                    placeholder="+234 801 234 5678"
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="addressLine1">Street Address</Label>
                <Input
                  id="addressLine1"
                  {...register('addressLine1')}
                  placeholder="123 Main Street"
                />
                {errors.addressLine1 && (
                  <p className="text-sm text-destructive">{errors.addressLine1.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="addressLine2">Apartment/Suite (Optional)</Label>
                <Input
                  id="addressLine2"
                  {...register('addressLine2')}
                  placeholder="Apt 4B"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    {...register('city')}
                    placeholder="Lagos"
                  />
                  {errors.city && (
                    <p className="text-sm text-destructive">{errors.city.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select onValueChange={(value) => setValue('state', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {nigerianStates.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.state && (
                    <p className="text-sm text-destructive">{errors.state.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code (Optional)</Label>
                <Input
                  id="postalCode"
                  {...register('postalCode')}
                  placeholder="100001"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isProcessing || !paymentVerified}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : !paymentVerified ? (
                  'Verifying Payment...'
                ) : (
                  'Confirm Order'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}